import { IS_LITE } from '../core/edition';
import * as THREE from 'three';
import { Input } from './actions';
import { PhysicsCity, type Hit } from '../physics/world';
import { GROUND, type Settings } from '../core/config';
import type { City } from '../world/city';
import { terrainHeight, type Terrain } from '../world/terrain';

/** One free god-view camera; no player body or hidden collision representation. */
export class Cameras {
  target = new THREE.Vector3(-55, 3, -40);
  distance = 135;
  yaw = 0.7;
  pitch = 0.76;
  private half = 210;
  private terrain?: Terrain;
  private initialTarget = new THREE.Vector3(-55, 3, -40);
  private initialDistance = 135;
  private raycaster = new THREE.Raycaster();
  private floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), -GROUND);

  constructor(
    readonly camera: THREE.PerspectiveCamera,
    readonly input: Input,
    readonly physics: PhysicsCity,
    readonly settings: Settings,
  ) {}

  setRegion(city: Pick<City, 'half' | 'terrain' | 'initialView' | 'initialTarget'>): void {
    this.half = IS_LITE ? 230 : city.half;
    this.terrain = city.terrain;
    if (city.initialTarget) this.initialTarget.set(city.initialTarget.x, 3, city.initialTarget.z);
    this.initialTarget.y = city.initialView?.targetY ?? 3;
    this.initialDistance = city.initialView?.distance ?? 135;
    this.camera.far = Math.max(this.camera.far, city.half * 5);
    this.camera.updateProjectionMatrix();
  }

  reset(): void {
    this.input.cancel();
    this.target.copy(this.initialTarget);
    this.distance = this.initialDistance;
    this.yaw = 0.7;
    this.pitch = 0.76;
    this.position();
  }

  overview(): void {
    this.input.cancel();
    this.target.set(0, 3, 0);
    this.distance = Math.max(135, this.half * (this.camera.aspect < 1 ? 3 : 2.45));
    this.yaw = 0.4;
    this.pitch = 1.02;
    this.position();
  }

  update(dt: number, _alpha: number): void {
    const input = this.input;
    const sensitivity = this.settings.sensitivity;
    this.yaw -= input.orbit.x * 0.004 * sensitivity;
    this.pitch = THREE.MathUtils.clamp(this.pitch + input.orbit.y * 0.004 * sensitivity, 0.18, 1.42);
    this.position();
    const beforeZoom = input.zoom !== 0 ? this.surfaceAtCursor() : null;
    let factor = Math.exp(input.zoom);
    if (beforeZoom && factor < 1)
      factor = Math.max(factor, Math.min(1, 1.25 / this.camera.position.distanceTo(beforeZoom)));
    if (factor > 1) factor = Math.min(factor, Math.max(650, this.half * 3) / this.distance);
    const scaledDistance = this.distance * factor;
    const nextDistance = Math.max(2, scaledDistance);
    if (beforeZoom) {
      // Move the eye towards the pointed surface even when the orbit radius
      // reaches its minimum. Radius is not the distance to the visible street
      // after panning away from an elevated roof or following an aircraft.
      const eyeOffset = this.camera.position.clone().sub(this.target).divideScalar(this.distance);
      this.target
        .sub(beforeZoom)
        .multiplyScalar(factor)
        .add(beforeZoom)
        .addScaledVector(eyeOffset, scaledDistance - nextDistance);
    }
    this.distance = nextDistance;
    this.position();
    if (beforeZoom) {
      // Terrain clearance can lift the focus. Restore the same pointer anchor
      // after that clamp, before applying independent pan/keyboard movement.
      this.raycaster.setFromCamera(input.aim, this.camera);
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -beforeZoom.y);
      const afterZoom = this.raycaster.ray.intersectPlane(plane, new THREE.Vector3());
      if (afterZoom)
        this.target.add(new THREE.Vector3(beforeZoom.x - afterZoom.x, 0, beforeZoom.z - afterZoom.z));
    }
    const panSurface = input.pan.lengthSq() > 0 ? this.surfaceAtCursor() : null;
    const panDistance = panSurface ? this.camera.position.distanceTo(panSurface) : this.distance;
    const scale =
      (2 * panDistance * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2))) /
      Math.max(1, this.input.canvas.clientHeight);
    const movement = input.moveVector();
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const ground = this.terrain
      ? terrainHeight(this.terrain, this.camera.position.x, this.camera.position.z)
      : GROUND;
    const travelDistance = Math.max(
      this.distance,
      (this.camera.position.y - (ground ?? GROUND)) / Math.sin(this.pitch),
    );
    const speed =
      Math.max(28, travelDistance * 0.36) *
      (input.keys.has('ShiftLeft') || input.keys.has('ShiftRight') ? 1.8 : 1);
    // Camera navigation follows wall time, even when a busy simulation renders
    // below 15 FPS. Bound a suspended/debugger frame to avoid a large jump.
    const elapsed = Math.min(dt, 0.25);
    this.target
      .addScaledVector(right, -input.pan.x * scale + movement.x * elapsed * speed)
      .addScaledVector(
        forward,
        (input.pan.y * scale) / Math.max(0.3, Math.sin(this.pitch)) + movement.y * elapsed * speed,
      );
    this.target.x = THREE.MathUtils.clamp(this.target.x, -this.half + 6, this.half - 6);
    this.target.z = THREE.MathUtils.clamp(this.target.z, -this.half + 6, this.half - 6);
    this.position();
    input.consume();
  }

  private position(): void {
    const targetHeight = this.terrain ? terrainHeight(this.terrain, this.target.x, this.target.z) : undefined;
    this.target.y = Math.max(this.target.y, (targetHeight ?? GROUND) + 0.45);
    this.camera.position.set(
      this.target.x + Math.sin(this.yaw) * Math.cos(this.pitch) * this.distance,
      this.target.y + Math.sin(this.pitch) * this.distance,
      this.target.z + Math.cos(this.yaw) * Math.cos(this.pitch) * this.distance,
    );
    const ground = this.terrain
      ? terrainHeight(this.terrain, this.camera.position.x, this.camera.position.z)
      : undefined;
    if (ground !== undefined) this.camera.position.y = Math.max(this.camera.position.y, ground + 2);
    this.camera.lookAt(this.target);
    this.camera.updateMatrixWorld();
  }

  private surfaceAtCursor(): THREE.Vector3 | null {
    this.raycaster.setFromCamera(this.input.aim, this.camera);
    const ray = this.raycaster.ray;
    return (
      this.physics.ray(ray.origin, ray.direction, this.camera.far)?.point.clone() ??
      ray.intersectPlane(this.floor, new THREE.Vector3())
    );
  }

  aim(): Hit | null {
    this.raycaster.setFromCamera(this.input.aim, this.camera);
    return this.physics.ray(this.raycaster.ray.origin, this.raycaster.ray.direction, this.camera.far);
  }
}
